/**
 * Editorial articles ("Zuprize Magasin").
 *
 * Source of truth for both the index page (`/magasin`) and individual
 * article pages (`/magasin/:slug`), plus the landing-page editorial
 * section. Authored inline here so we can iterate fast and ship without
 * a CMS round-trip; swapping to a hosted CMS later is mechanical — the
 * page components only depend on the shape below.
 *
 * Block schema (see `blocks` per article):
 *   { type: 'heading',      level: 2|3, text }
 *   { type: 'paragraph',    text }                       // plain
 *   { type: 'paragraph',    segments: [{ text, bold?, italic?, href? }, ...] }
 *   { type: 'list',         items: [string | { lead, text }] }
 *   { type: 'ordered-list', items: [string | { lead, text }] }
 *   { type: 'image',        images: [{ src, alt, width, height }] }  // 1 or 2
 *   { type: 'pullout',      text }                       // italic PS / aside
 *   { type: 'cta',          href, label }                // green button
 *
 * Adding an article = append an entry. Order here drives the index grid
 * order (newest first → keep newest at the top). The sitemap lists every
 * article from this list too (server/resources/sitemap.js), so plain
 * CommonJS: the server requires it as well as the web app.
 */

const POST_LISTING_HREF = '/l/new';
const POST_LISTING_LABEL = 'Legg ut ditt første plagg';

const editorials = [
  {
    slug: 'slik-tar-du-proffe-annonsebilder-med-mobilen',
    eyebrow: 'ZUPRIZE MAGASIN \u00b7 Guide',
    title:
      'Tror du det er vanskelig \u00e5 ta gode bilder? Slik tar du proffe annonsebilder med mobilen',
    lede:
      'Sitter du p\u00e5 fine klesplagg, sko eller tilbeh\u00f8r som fortjener et nytt hjem, men n\u00f8ler med \u00e5 legge det ut p\u00e5 Zuprize fordi du tror bildene m\u00e5 se profesjonelle ut?',
    image: '/static/images/editorial/foto-guide-hero.avif',
    imageAlt:
      'Kvinne fotograferer en beige strikkegenser som ligger flatt p\u00e5 en seng, med mobilen.',
    publishedAt: '2026-09-23',
    readingMinutes: 3,
    blocks: [
      {
        type: 'paragraph',
        segments: [
          { text: 'La oss avkrefte myten med en gang: ' },
          {
            text: 'Du trenger verken dyrt fotoutstyr, fotostudio eller teknisk innsikt.',
            bold: true,
          },
        ],
      },
      {
        type: 'paragraph',
        text:
          'Mobiltelefonen du allerede har i h\u00e5nden har mer enn god nok kamerakvalitet. Det viktigste handler ikke om megapiksler, men om sm\u00e5, enkle grep du gj\u00f8r hjemme i stua. Med disse fire trinnene tar du bilder som skiller seg ut i feeden og gj\u00f8r tingene dine lettsolgte!',
      },

      { type: 'heading', level: 3, text: 'Trinn 1: Bruk naturlig lys (Dropp blitsen!)' },
      {
        type: 'paragraph',
        text:
          'Det enkleste grepet for klare og fine farger. Glem taklamper og blits p\u00e5 mobilen. Det gir ofte gult lys, harde skygger og uekte farger.',
      },
      {
        type: 'list',
        items: [
          {
            lead: 'Finn et vindu:',
            text:
              'Legg eller heng varen i n\u00e6rheten av et stort vindu p\u00e5 dagtid. Naturlig dagslys gir myke skygger og viser den faktiske fargen p\u00e5 produktet.',
          },
          {
            lead: 'Skygge er din venn:',
            text:
              'Sterkt, direkte sollys kan skape for harde kontraster. Indirekte dagslys er det aller beste.',
          },
        ],
      },

      { type: 'heading', level: 3, text: 'Trinn 2: Rydd bakgrunnen' },
      {
        type: 'paragraph',
        text:
          'La varen v\u00e6re stjernen i bildet. Kj\u00f8perne vil se hva de betaler for - ikke rotet p\u00e5 kj\u00f8kkenbenken eller klesvasken i bakgrunnen.',
      },
      {
        type: 'list',
        items: [
          {
            lead: 'Hold det n\u00f8ytralt:',
            text:
              'Bruk et lyst tregulv, et n\u00f8ytralt sengeteppe, et hvitt laken eller en ren vegg som bakgrunn.',
          },
          {
            lead: 'Presentasjon:',
            text:
              'Heng klesplagg pent p\u00e5 en trefarget eller n\u00f8ytral kleshenger mot en ren vegg, eller legg dem flatt opp\u00e5 et lyst underlag (\u00abflatlay\u00bb).',
          },
        ],
      },
      {
        type: 'image',
        images: [
          {
            src: '/static/images/editorial/foto-guide-jeans.avif',
            alt:
              'Lysebl\u00e5 jeans lagt flatt p\u00e5 et lyst tregulv, fotografert rett ovenfra.',
            width: 1086,
            height: 1448,
          },
        ],
      },

      { type: 'heading', level: 3, text: 'Trinn 3: T\u00f8rk av linsen og finn vinklene' },
      {
        type: 'paragraph',
        text:
          'Et usynlig triks som gj\u00f8r underverker. Dette trikset glemmer nesten alle: T\u00f8rk over mobilkameralinsen med genseren eller en mikrofiberklut. Mobilkameraer samler opp fettflekker fra fingrene v\u00e5re, og en ren linse gj\u00f8r bildet umiddelbart skarpt!',
      },
      {
        type: 'list',
        items: [
          {
            lead: 'Ta et oversiktsbilde f\u00f8rst:',
            text: 'Dette blir hovedbildet i annonsen din p\u00e5 Zuprize.',
          },
          {
            lead: 'Vis detaljene:',
            text:
              'Ta n\u00e6rbilder av stoffm\u00f8nster, merkelapp med st\u00f8rrelse/vaskeanvisning og merke.',
          },
          {
            lead: 'V\u00e6r \u00e6rlig:',
            text:
              'Har genseren noen nupper, eller jakken litt slitasje ved glidel\u00e5sen? Ta bilde av det! Kj\u00f8pere setter enormt pris p\u00e5 \u00e6rlighet, og det forhindrer misforst\u00e5elser.',
          },
        ],
      },
      {
        type: 'image',
        images: [
          {
            src: '/static/images/editorial/foto-guide-detalj-knapp.avif',
            alt: 'N\u00e6rbilde av en knapp p\u00e5 et rutete ullstoff.',
            width: 1066,
            height: 1600,
          },
          {
            src: '/static/images/editorial/foto-guide-detalj-broderi.avif',
            alt: 'N\u00e6rbilde av hvitt blomsterbroderi p\u00e5 en m\u00f8rk denimjakke.',
            width: 1125,
            height: 750,
          },
        ],
      },

      { type: 'heading', level: 3, text: 'Trinn 4: Gj\u00f8r enkle justeringer p\u00e5 mobilen' },
      {
        type: 'paragraph',
        text:
          'Ingen avansert redigering n\u00f8dvendig. Du trenger ikke avanserte fotoredigeringsapper eller tunge filtre.',
      },
      {
        type: 'list',
        items: [
          'Trykk p\u00e5 skjermen der motivet er f\u00f8r du knipser for \u00e5 sikre at fokus og lysstyrke blir riktig.',
          'Hvis bildet virker litt m\u00f8rkt, kan du enkelt justere lysstyrken (\u00abEksponering\u00bb) ett hakk opp direkte i mobilens eget fotoalbum.',
          'Unng\u00e5 fargefiltre \u2013 kj\u00f8peren vil se varen slik den ser ut i virkeligheten.',
        ],
      },

      {
        type: 'paragraph',
        segments: [
          { text: 'S\u00e5, hva venter du p\u00e5?', bold: true },
          {
            text:
              ' Grip mobilen, finn frem det f\u00f8rste du vil rydde bort, og la dagslyset gj\u00f8re jobben for deg. Det tar ikke mer enn et par minutter - og vips, s\u00e5 er din f\u00f8rste annonse klar p\u00e5 Zuprize. Masse lykke til!',
          },
        ],
      },
      { type: 'cta', href: POST_LISTING_HREF, label: POST_LISTING_LABEL },
    ],
  },
  {
    slug: 'fire-grunner-til-a-rydde-skapet',
    eyebrow: 'ZUPRIZE MAGASIN · Tips',
    title: '4 grunner til å rydde skapet - og tjene på det',
    lede:
      'Ta en titt i garderobeskapet ditt. Hvor mange plagg har du som du ikke har hatt på deg det siste året, og hva tror du de er verdt til sammen? Mange har skap fulle av klær og sko som ikke blir brukt. Hva sier du til å få bedre samvittighet og tjene litt penger på det? Og det beste av alt: Det går fortere enn du tror.',
    image: '/static/images/editorial/wardrobe-fire-grunner.avif',
    imageAlt:
      'Kvinne fotograferer en lys skjorte på henger i et solfylt garderobeskap med mobilen.',
    publishedAt: '2026-09-10',
    readingMinutes: 3,
    blocks: [
      {
        type: 'paragraph',
        segments: [
          {
            text:
              'Zuprize gjennomførte våren 2026 en undersøkelse som viser at mange kvinner ønsker å selge klær de ikke bruker, men at 6 av 10 kvinner opplever tid som det største hinderet. Det trenger ikke å være vanskelig å selge klærne dine! Bruk ',
          },
          { text: 'Zuprize', href: '/' },
          { text: ', så tar det deg kun to minutter å legge ut et plagg til salgs.' },
        ],
      },
      { type: 'paragraph', text: 'Her er fire fordeler ved å rydde i skapet:' },
      {
        type: 'ordered-list',
        items: [
          {
            lead: 'Du sitter på en formue.',
            text:
              'Tenk på skapet som en konto. Hvert plagg har en verdi, men akkurat nå henger pengene bare der. Et plagg du ikke bruker, gir deg ingenting. Det tar bare plass. Selg det videre, og bruk pengene på noe du trenger eller har lyst til å spare til. En enkel rettesnor er: Behold plaggene du virkelig er glad i, og som føles riktige å ha på. La resten få et nytt liv hos noen som vil bruke dem.',
          },
          {
            lead: 'Det er enkelt å selge dine brukte klær.',
            text:
              'Dette er hele poenget: Mange opplever tid som hindringen, og den har vi fjernet. På Zuprize legger du ut et plagg på under to minutter fra mobilen. Du tar et par bilder av plagget, og resten går nesten av seg selv. Du får hjelp til å lage annonseteksten og tips til hvilke bilder det er lurt å ta av plagget du vil selge. Begynn med ett plagg du ikke bruker. Snart kan det være på vei til en ny eier og penger på vei inn på din konto.',
          },
          {
            lead: 'Et ryddig skap gir en god følelse.',
            text:
              'Når du har oversikt, blir det enklere å finne frem, og enklere å bruke det du faktisk liker. Et skap der alle plagg har en funksjon, er rett og slett lettere å leve med. Når et plagg får en ny eier, får det også leve videre. Det er bra for miljøet og sirkulering i praksis: Det som allerede finnes, blir brukt før noe nytt må lages.',
          },
          {
            lead: 'Du blir mer bevisst på eget forbruk.',
            text:
              'Når du går gjennom skapet for å selge, får du oversikt over hva du har og faktisk bruker. SIFO-tall viser at vi i snitt har over 350 plagg i garderoben, og at hvert femte plagg sjelden eller aldri tas i bruk. Ser du etter, finner du ofte et mønster: Plaggene du bruker mest, er gjerne de som føles som deg. Det som blir hengende, er ofte kjøp som kanskje ikke var så gjennomtenkte. Før du starter handleturen i butikken, start i ditt eget skap. Hva har du, og hva trenger du å komplettere med?',
          },
        ],
      },
      {
        type: 'paragraph',
        text:
          'En funksjonell og smart garderobe handler om å ha plagg som kan brukes sammen, som passer deg og som du faktisk har lyst til å bruke.',
      },
      {
        type: 'paragraph',
        segments: [
          {
            text:
              'Husk, du trenger ikke å tømme hele skapet med en gang. Sett av noen minutter til å finne et plagg du ikke har brukt på lenge og gå inn på ',
          },
          { text: 'Zuprize', href: '/' },
          {
            text:
              '. På noen minutter har du kanskje begynt opprydningen av skapet, sirkulert plagg du ikke bruker og tjent penger.',
          },
        ],
      },
      { type: 'cta', href: POST_LISTING_HREF, label: POST_LISTING_LABEL },
    ],
  },
  {
    slug: 'kunsten-a-rydde',
    eyebrow: 'ZUPRIZE MAGASIN · Ryddeguide',
    title: 'Slik realiserer du verdiene i klesskapet ditt — en guide til hvordan du rydder',
    lede:
      'Å rydde i garderoben handler ikke om å tømme skapet i sorte søppelsekker. Det handler om å kuratere — å skape en inspirerende garderobe med plagg du liker og bruker, samtidig som du er bevisst hvilken verdi de har.',
    image: '/static/images/editorial/wardrobe-kunsten-a-rydde.avif',
    imageAlt:
      'En lys, ryddig garderobe med klær hengende mot en hvit vegg — illustrasjon av en kuratert klesskap.',
    publishedAt: '2026-06-24',
    readingMinutes: 5,
    blocks: [
      {
        type: 'paragraph',
        text:
          'Vi har laget en guide til hvordan du går frem, steg for steg, for å skape orden, og samtidig kickstarte deg til å elske å selge klær du ikke bruker.',
      },
      { type: 'heading', level: 2, text: 'Forberedelse: sett av tid — og sett på musikk' },
      {
        type: 'paragraph',
        text:
          'Sett av to timer en ettermiddag eller formiddag. Tøm hele eller deler av klesskapet, sett på god musikk, og tenk at målet ikke bare er orden, men å frigjøre verdier og bidra til at flere plagg av god kvalitet sirkulerer.',
      },
      { type: 'heading', level: 2, text: 'Steg 1: Zuprize-testen — fire kontrollspørsmål' },
      {
        type: 'paragraph',
        text: 'Når du tar opp hvert enkelt plagg, skal du stille deg selv fire spørsmål.',
      },
      {
        type: 'list',
        items: [
          {
            lead: 'Når brukte jeg det sist?',
            text:
              'Hvis plagget har hengt urørt det siste året, er sjansen stor for at det ikke lenger tjener deg.',
          },
          {
            lead: 'Passer det min nåværende kropp og livsstil?',
            text:
              'Vi endrer oss, og garderoben bør reflektere den vi er i dag — ikke den man var for fem år siden.',
          },
          {
            lead: 'Kan jeg style det på tre ulike måter med det jeg allerede eier?',
            text:
              'Hvis ikke, ut. En funksjonell garderobe handler om å ha plagg som kan brukes om hverandre.',
          },
          {
            lead: 'Føler jeg meg bra i det?',
            text:
              'Klær skal gi deg selvtillit og komfort. Hvis en bluse klemmer feil eller en bukse krever at du holder pusten, har den ingenting i hverdagen din å gjøre.',
          },
        ],
      },
      { type: 'heading', level: 2, text: 'Steg 2: sortering med mening' },
      {
        type: 'paragraph',
        text:
          'Del klærne dine inn i fire tydelige kategorier og gjør deg klar til å sortere.',
      },
      {
        type: 'list',
        items: [
          {
            lead: 'Beholde.',
            text:
              'Plaggene som bestod testen, de som passer, brukes ofte, og som du enkelt kan style med resten av garderoben din. Heng dem tilbake med luft mellom hengerne.',
          },
          {
            lead: 'Selge på Zuprize.',
            text:
              'Kvalitetsplaggene som er pent brukt og i god stand, men som av en eller annen grunn ikke blir brukt av deg. Kanskje stilen din har endret seg, eller størrelsen ble feil? Ved å legge dem ut på Zuprize gir du en annen kvinne muligheten til å oppdage et plagg hun vil elske, samtidig som du frigjør penger til å kjøpe noe du trenger.',
          },
          {
            lead: 'Reparere eller rense.',
            text:
              'Plagget du egentlig elsker, men som mangler en knapp, har en løs fald eller trenger en profesjonell rens. Legg disse i en egen bunke. Det er utrolig hvor mye glede som ligger i å gi et eksisterende plagg litt omsorg for videre bruk.',
          },
          {
            lead: 'Gi som en gave.',
            text:
              'Noen plagg har en helt spesiell verdi eller historie, og det føles ekstra fint å gi dem videre som en gjennomtenkt gave til en datter, en søster eller en nær venninne. Å arve et plagg som har levd et liv, og som bærer med seg en historie, har en helt egen sjarm og emosjonell verdi.',
          },
        ],
      },
      { type: 'heading', level: 2, text: 'Steg 3: fra ryddekaos til Zuprize-butikk' },
      {
        type: 'paragraph',
        text:
          'Når du har sortert ferdig det som skal selges, har du allerede gjort den største jobben — du er snart i mål!',
      },
      {
        type: 'ordered-list',
        items: [
          {
            lead: 'Se på plaggene med en kjøpers øyne.',
            text: 'Stryk eller damp blusen, og se over at alt er rent og pent.',
          },
          {
            lead: 'Ta bildene med en gang.',
            text:
              'Ta med klærne til et sted som har godt, naturlig dagslys. Heng plaggene på en henger mot en nøytral vegg, eller flatt på et nøytralt underlag.',
          },
          {
            lead: 'Skriv historien.',
            text:
              'Når bildene er tatt, tar det under to minutter å legge ut en annonse på Zuprize. Skriv gjerne litt om din erfaring: hvordan plagget er i størrelsen, eller hvordan du har stylet det.',
          },
        ],
      },
      { type: 'heading', level: 2, text: 'Klar for å komme i gang?' },
      {
        type: 'paragraph',
        text:
          'Du trenger ikke å gjøre alt på én dag, men ta frem mobilen og legg ut det første plagget. Kanskje du blir overrasket over hvor raskt og enkelt det gikk?',
      },
      { type: 'cta', href: POST_LISTING_HREF, label: POST_LISTING_LABEL },
    ],
  },
];

/** Look up a single editorial by slug. Returns null if not found. */
const findEditorial = slug => editorials.find(e => e.slug === slug) || null;

module.exports = { editorials, findEditorial };
